import { createPinia, type Pinia } from 'pinia';
import type { App } from 'vue';
import { BaseService } from '../base';

// Store 服务配置
export interface StoreServiceConfig {
  // Pinia 配置选项
  pinia?: {
    // 可以添加 Pinia 的配置选项
    // 例如：插件、持久化等
  };
  // Store 配置选项
  stores?: {
    // 可以添加 Store 的配置选项
  };
}

// Store 服务实现
export class StoreService extends BaseService {
  private pinia: Pinia | null = null;

  constructor(_config: StoreServiceConfig = {}) {
    super('StoreService');
  }

  // 初始化服务
  protected async onInitialize(): Promise<void> {
    this.pinia = this.createPinia();
  }

  // 销毁服务
  protected async onDestroy(): Promise<void> {
    // 注意：Pinia store 一旦安装到 Vue 应用，通常不需要手动清理
    // 当 Vue 应用被销毁时，Pinia 会自动清理
    this.pinia = null;
  }

  // 创建 Pinia 实例
  createPinia(): Pinia {
    if (this.pinia) {
      return this.pinia;
    }

    this.pinia = createPinia();
    return this.pinia;
  }

  // 安装到 Vue 应用
  install(app: App): void {
    const pinia = this.createPinia();
    app.use(pinia);
  }

  // 获取 Pinia 实例
  getPinia(): Pinia | null {
    return this.pinia;
  }
}

// 创建 Store 服务实例
export const createStoreService = (config: StoreServiceConfig = {}): StoreService => {
  return new StoreService(config);
};

// 全局 Store 服务实例
let globalStoreService: StoreService | null = null;

// 初始化 Store 服务
export const initStoreService = async (config: StoreServiceConfig = {}): Promise<StoreService> => {
  // SSR 环境下为每个请求创建新实例，避免跨请求共享状态
  if (import.meta.server) {
    const newService = createStoreService(config);
    await newService.initialize();
    return newService;
  }

  if (!globalStoreService) {
    globalStoreService = createStoreService(config);
    await globalStoreService.initialize();
  }
  return globalStoreService;
};

// 获取 Store 服务实例
export const getStoreService = (): StoreService => {
  if (!globalStoreService) {
    throw new Error('StoreService not initialized. Call initStoreService first.');
  }
  return globalStoreService;
};

// SSR 环境下重置全局实例（用于请求结束时清理）
export const resetGlobalStoreService = (): void => {
  // SSR 中每次创建新实例，不需要清理全局引用
  // 但为了安全起见，确保全局引用被清理
  if (import.meta.server) {
    globalStoreService = null;
  }
};
